class BombeCampaignVideo extends HTMLElement {
  connectedCallback() {
    this.videos = [...this.querySelectorAll('video')];
    this.mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.isTikTokWebView = /BytedanceWebview|musical_ly|trill_/i.test(navigator.userAgent);
    this.debugEnabled = Boolean(window.Shopify?.designMode || new URLSearchParams(window.location.search).get('bombe_video_debug') === '1');
    this.isNearViewport = false;
    this.playStates = new WeakMap();
    this.updatePlayback = this.updatePlayback.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleLifecycleResume = this.handleLifecycleResume.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleVideoReady = this.handleVideoReady.bind(this);
    this.handleVideoPlaying = this.handleVideoPlaying.bind(this);
    this.handleTikTokViewportEvent = this.handleTikTokViewportEvent.bind(this);

    this.mediaQuery.addEventListener('change', this.updatePlayback);
    window.addEventListener('pageshow', this.handleLifecycleResume);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    for (const video of this.videos) {
      this.configureVideo(video);
      this.playStates.set(video, { pending: false });
      video.addEventListener('loadedmetadata', this.handleVideoReady);
      video.addEventListener('canplay', this.handleVideoReady);
      video.addEventListener('playing', this.handleVideoPlaying);
    }

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.handleIntersection, {
        rootMargin: this.isTikTokWebView ? '600px 0px' : '300px 0px',
        threshold: [0, 0.1],
      });
      this.observer.observe(this);
    }

    if (this.isTikTokWebView || !this.observer) {
      window.addEventListener('scroll', this.handleTikTokViewportEvent, { passive: true });
      window.addEventListener('resize', this.handleTikTokViewportEvent, { passive: true });
    }

    this.debug('TikTok WebView detected', { detected: this.isTikTokWebView });
    this.refreshViewportState('connected');
  }

  disconnectedCallback() {
    this.mediaQuery?.removeEventListener('change', this.updatePlayback);
    window.removeEventListener('pageshow', this.handleLifecycleResume);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('scroll', this.handleTikTokViewportEvent);
    window.removeEventListener('resize', this.handleTikTokViewportEvent);
    this.observer?.disconnect();

    if (this.viewportFrame) cancelAnimationFrame(this.viewportFrame);

    for (const video of this.videos || []) {
      video.removeEventListener('loadedmetadata', this.handleVideoReady);
      video.removeEventListener('canplay', this.handleVideoReady);
      video.removeEventListener('playing', this.handleVideoPlaying);
    }
  }

  configureVideo(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;
    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    if (video.dataset.bombeAutoplay === 'true' && !this.mediaQuery.matches) {
      video.autoplay = true;
      video.setAttribute('autoplay', '');
    }
  }

  isVideoRenderable(video) {
    const style = window.getComputedStyle(video);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  shouldPlay(video) {
    return (
      video.dataset.bombeAutoplay === 'true' &&
      !this.mediaQuery.matches &&
      !document.hidden &&
      this.isNearViewport &&
      this.isVideoRenderable(video)
    );
  }

  attemptPlay(video, reason) {
    if (!this.shouldPlay(video)) return;

    const state = this.playStates.get(video) || { pending: false };
    if (state.pending || (!video.paused && video.readyState >= 3)) return;

    this.configureVideo(video);
    state.pending = true;
    this.playStates.set(video, state);

    let playResult;
    try {
      playResult = video.play();
    } catch (error) {
      state.pending = false;
      this.debug('play rejected', { reason, error: error?.name || 'Error' });
      return;
    }

    if (!playResult || typeof playResult.then !== 'function') {
      state.pending = false;
      this.debug('play fulfilled', { reason });
      return;
    }

    playResult
      .then(() => {
        this.debug('play fulfilled', { reason });
      })
      .catch((error) => {
        this.debug('play rejected', { reason, error: error?.name || 'Error' });
      })
      .finally(() => {
        state.pending = false;
      });
  }

  updatePlayback(reason = 'update') {
    for (const video of this.videos) {
      if (this.mediaQuery.matches) {
        video.pause();
        video.autoplay = false;
        video.removeAttribute('autoplay');
        continue;
      }

      if (this.shouldPlay(video)) {
        this.attemptPlay(video, reason);
      } else {
        video.pause();
      }
    }
  }

  refreshViewportState(reason) {
    const rect = this.getBoundingClientRect();
    const margin = this.isTikTokWebView ? 600 : 300;
    const wasNearViewport = this.isNearViewport;
    this.isNearViewport = rect.bottom >= -margin && rect.top <= window.innerHeight + margin;

    if (reason !== 'tiktok-viewport' || this.isNearViewport !== wasNearViewport) {
      this.updatePlayback(reason);
    }
  }

  handleIntersection(entries) {
    const entry = entries[0];
    if (!entry) return;

    this.isNearViewport = entry.isIntersecting;
    this.updatePlayback('intersection');
  }

  handleTikTokViewportEvent() {
    if (this.viewportFrame) return;

    this.viewportFrame = requestAnimationFrame(() => {
      this.viewportFrame = 0;
      this.refreshViewportState('tiktok-viewport');
    });
  }

  handleLifecycleResume() {
    this.refreshViewportState('pageshow');
  }

  handleVisibilityChange() {
    if (document.hidden) {
      for (const video of this.videos) video.pause();
      return;
    }

    this.refreshViewportState('visibilitychange');
  }

  handleVideoReady(event) {
    this.debug(event.type, { readyState: event.currentTarget.readyState });
    this.attemptPlay(event.currentTarget, event.type);
  }

  handleVideoPlaying(event) {
    this.debug('playing', { readyState: event.currentTarget.readyState });
  }

  debug(message, detail = {}) {
    if (!this.debugEnabled) return;
    console.debug('[BOMBÉ editorial video]', message, detail);
  }
}

if (!customElements.get('bombe-campaign-video')) {
  customElements.define('bombe-campaign-video', BombeCampaignVideo);
}
